import { z } from 'zod';

export const CreateResponseInputSchema = z.object({
  response: z.union([
    z.object({
      type: z.literal('final').describe('Final or end-of-turn response message.'),
      kind: z
        .enum(['informational', 'error'])
        .default('informational')
        .optional()
        .describe(
          `The type of message to generate. Default to "informational" for regular responses. Use "error" when explaining an error or failure condition, or if you're unable to answer the user's prompt due to technical limitations.`,
        ),
      message: z
        .string()
        .describe(
          `Your Markdown formatted response message. Your response should be concise, clear, and directly address the user's query or request. Translate any internal or technical identifiers to plain English: instead of saying "monthAllocProdAllocatedOil", say "monthly allocated oil production"; instead of "externalId", say "external ID". Write dates and times in plain language. For example, "June 11", "mid-May", "late March 2026". Do not mention raw ISO 8601 or epoch timestamps in prose. Always translate PascalCased, camelCased, kebab-cased, underscore_cased or other internal identifiers to plain english equivalents.`,
        ),
      nextSteps: z
        .string()
        .array()
        .min(0)
        .max(5)
        .optional()
        .describe(
          '<5 suggested follow-up user prompts, next steps and questions the user can ask based on the information provided in the `message` parameter. Examples: "What is the expected production for next month?", "Can you provide a breakdown of production by well?", "What are the main factors affecting production this month?".',
        ),
    }),
    z.object({
      type: z.literal('in-progress').describe('In-progress or mid-turn response message.'),
      message: z
        .string()
        .describe(
          'Concise Markdown formatted message. In-progress messages are optional informational messages to inform the user about progress during multi-tool turns, and should contain at most 1-2 sentences.',
        ),
    }),
  ]),
});

export const CreateResponseOutputSchema = z.object({
  success: z.literal(true),
});

export type CreateResponseInput = z.infer<typeof CreateResponseInputSchema>;
export type CreateResponseOutput = z.infer<typeof CreateResponseOutputSchema>;
